// Nursing assessment (A3) and repeatable risk screens (A3/C5).
// Phase 3 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§5.3/§6.7).
//
// Score/band are never computed here — every number on screen came back
// from the server's own POST response. This component only renders what
// the server already decided, so there is no way for a display bug to
// disagree with what gets printed later.
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import {
  useNursingAssessment, useSaveNursingAssessment, useSignNursingAssessment,
  useRiskScreens, useCreateRiskScreen,
} from '../hooks';
import type { RiskScreenTool, RiskScreenType } from '../types';
import { Section, Empty, fmtDateTime, useCan } from '../ui';

function useErr() {
  const [err, setErr] = useState<string | null>(null);
  const fail = (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Something went wrong.');
  const node = err ? <div className="mb-2 rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div> : null;
  return { fail, node, clear: () => setErr(null) };
}

// One row per screen in the A3 table — its default tool and a few basic
// label-only item fields. Doctor-only VTE is enforced server-side; this
// list is just what the "+ Screen" picker offers.
const SCREENS: { type: RiskScreenType; label: string; tool: RiskScreenTool; toolLabel: string }[] = [
  { type: 'pain', label: 'Pain', tool: 'nrs', toolLabel: 'NRS 0-10' },
  { type: 'nutrition', label: 'Nutrition', tool: 'nrs2002', toolLabel: 'NRS-2002' },
  { type: 'fall', label: 'Fall risk', tool: 'morse', toolLabel: 'Morse' },
  { type: 'pressure_injury', label: 'Pressure injury', tool: 'braden', toolLabel: 'Braden' },
  { type: 'vte', label: 'VTE', tool: 'padua', toolLabel: 'Padua' },
  { type: 'functional', label: 'Functional', tool: 'barthel', toolLabel: 'Barthel' },
];

const BAND_TONE = (band: string): 'success' | 'warn' | 'danger' | 'neutral' => {
  const b = band.toLowerCase();
  if (b.includes('high') || b.includes('severe') || b.includes('total')) return 'danger';
  if (b.includes('moderate') || b.includes('medium') || b.includes('mild')) return 'warn';
  if (b.includes('low') || b.includes('no risk') || b.includes('independent') || b.includes('not currently')) return 'success';
  return 'neutral';
};

export function RiskScreensSection({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  return (
    <div className="space-y-4">
      <NursingAssessmentCard admissionId={admissionId} readOnly={readOnly} />
      <RiskScreensCard admissionId={admissionId} readOnly={readOnly} />
    </div>
  );
}

function NursingAssessmentCard({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const mayWrite = can('ip:nursing-assessment') && !readOnly;
  const { data, isLoading } = useNursingAssessment(admissionId);
  const save = useSaveNursingAssessment(admissionId);
  const sign = useSignNursingAssessment(admissionId);
  const { fail, node, clear } = useErr();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ arrival_mode: '', consciousness: '', skin_findings: '' });

  const isSigned = data?.status === 'signed';
  const startEdit = () => { setDraft({ arrival_mode: data?.arrival_mode ?? '', consciousness: data?.consciousness ?? '', skin_findings: data?.skin_findings ?? '' }); setEditing(true); clear(); };

  if (isLoading) return <Section title="Initial nursing assessment"><LoadingState label="Loading…" /></Section>;

  return (
    <Section
      title="Initial nursing assessment"
      subtitle={data ? (isSigned ? `Signed · ${fmtDateTime(data.signed_at)}` : 'Draft — not yet signed') : 'Not started'}
      action={
        <>
          {mayWrite && !editing && !isSigned && <Button size="sm" onClick={startEdit}>{data ? 'Continue' : 'Start'}</Button>}
          {mayWrite && data && !isSigned && !editing && (
            <Button variant="success" size="sm" loading={sign.isPending} onClick={() => sign.mutate(undefined, { onError: fail })}>Sign</Button>
          )}
        </>
      }
    >
      {node}
      {!data && !editing ? (
        <Empty label="No nursing assessment recorded" />
      ) : editing ? (
        <div className="space-y-2">
          <div className="grid gap-2 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold">Arrival mode</label>
              <NativeSelect value={draft.arrival_mode} onChange={e => setDraft(p => ({ ...p, arrival_mode: e.target.value }))}>
                <option value="">— select —</option>
                <option value="walk">Walk</option>
                <option value="wheelchair">Wheelchair</option>
                <option value="stretcher">Stretcher</option>
              </NativeSelect>
            </div>
            <div><label className="mb-1 block text-xs font-semibold">Consciousness</label>
              <Input value={draft.consciousness} onChange={e => setDraft(p => ({ ...p, consciousness: e.target.value }))} /></div>
            <div><label className="mb-1 block text-xs font-semibold">Skin findings</label>
              <Input value={draft.skin_findings} onChange={e => setDraft(p => ({ ...p, skin_findings: e.target.value }))} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" loading={save.isPending} onClick={() => {
              clear();
              save.mutate(draft, { onSuccess: () => setEditing(false), onError: fail });
            }}>Save</Button>
          </div>
        </div>
      ) : data ? (
        <div className="grid gap-2 text-[12.5px] sm:grid-cols-3">
          <div><span className="text-ink-muted">Arrival: </span>{data.arrival_mode ?? '—'}</div>
          <div><span className="text-ink-muted">Consciousness: </span>{data.consciousness ?? '—'}</div>
          <div><span className="text-ink-muted">Skin: </span>{data.skin_findings ?? '—'}</div>
        </div>
      ) : null}
    </Section>
  );
}

function RiskScreensCard({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const mayWrite = can('ip:risk-screen') && !readOnly;
  const { data, isLoading } = useRiskScreens(admissionId);
  const create = useCreateRiskScreen(admissionId);
  const { fail, node, clear } = useErr();
  const [adding, setAdding] = useState<RiskScreenType | null>(null);
  const [items, setItems] = useState<Record<string, unknown>>({});

  const screenDef = SCREENS.find(s => s.type === adding);
  const setItem = (k: string, v: unknown) => setItems(p => ({ ...p, [k]: v }));

  const submit = () => {
    if (!screenDef) return;
    clear();
    create.mutate({ screen_type: screenDef.type, tool: screenDef.tool, items }, {
      onSuccess: () => { setAdding(null); setItems({}); },
      onError: fail,
    });
  };

  return (
    <Section
      focusId="risk_screen"
      title="Risk screens"
      subtitle="Score and band are always computed by the server from the item answers below — never trusted from the browser."
      action={mayWrite && !adding && (
        <NativeSelect data-focus="risk_screen:new" className="w-40" value="" onChange={e => { setAdding(e.target.value as RiskScreenType); setItems({}); }}>
          <option value="">+ New screen…</option>
          {SCREENS.map(s => <option key={s.type} value={s.type}>{s.label} ({s.toolLabel})</option>)}
        </NativeSelect>
      )}
    >
      {adding && screenDef && (
        <div className="mb-3 rounded-lg border border-border bg-muted/40 p-3">
          <div className="mb-2 text-[12.5px] font-semibold">{screenDef.label} — {screenDef.toolLabel}</div>
          <ScreenItemsForm tool={screenDef.tool} items={items} setItem={setItem} />
          {node}
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setAdding(null); clear(); }}>Cancel</Button>
            <Button size="sm" loading={create.isPending} onClick={submit}>Record screen</Button>
          </div>
        </div>
      )}
      {!adding && node}

      {isLoading ? <LoadingState label="Loading risk screens…" />
        : !data?.length ? <Empty label="No risk screens recorded" />
        : (
          <div className="space-y-2">
            {data.map(s => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border border-border px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-semibold">{SCREENS.find(x => x.type === s.screen_type)?.label ?? s.screen_type}</span>
                  <span className="text-[11px] text-ink-muted">({s.tool})</span>
                  <Badge variant={BAND_TONE(s.band)}>{s.band} · {s.score}</Badge>
                </div>
                <div className="text-[11px] text-ink-muted">
                  {fmtDateTime(s.screened_at)}{s.screened_by_name ? ` · ${s.screened_by_name}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
    </Section>
  );
}

/** Item inputs per scorable tool — deliberately plain, matching
 * ipRiskScores.js's own field names 1:1 so there is no translation layer
 * between what this form sends and what the server scores. */
function ScreenItemsForm({ tool, items, setItem }: { tool: RiskScreenTool; items: Record<string, unknown>; setItem: (k: string, v: unknown) => void }) {
  const bool = (k: string, label: string) => (
    <label key={k} className="flex items-center gap-1.5 text-[12px]">
      <input type="checkbox" checked={!!items[k]} onChange={e => setItem(k, e.target.checked)} />
      {label}
    </label>
  );
  const select4 = (k: string, label: string, max: number) => (
    <div key={k}>
      <label className="mb-1 block text-[11px] text-ink-muted">{label} (1-{max})</label>
      <NativeSelect className="w-20" value={String(items[k] ?? '')} onChange={e => setItem(k, Number(e.target.value))}>
        <option value="">—</option>
        {Array.from({ length: max }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
      </NativeSelect>
    </div>
  );

  if (tool === 'nrs') {
    return (
      <div>
        <label className="mb-1 block text-[11px] text-ink-muted">Pain score (0-10)</label>
        <Input type="number" min={0} max={10} className="w-20" value={String(items.score ?? '')} onChange={e => setItem('score', Number(e.target.value))} />
      </div>
    );
  }
  if (tool === 'morse') {
    return (
      <div className="flex flex-wrap gap-3">
        {bool('history_of_falling', 'History of falling')}
        {bool('secondary_diagnosis', '≥2 diagnoses')}
        {bool('iv_heparin_lock', 'IV/heparin lock')}
        <div>
          <label className="mb-1 block text-[11px] text-ink-muted">Ambulatory aid</label>
          <NativeSelect className="w-40" value={String(items.ambulatory_aid ?? 'none')} onChange={e => setItem('ambulatory_aid', e.target.value)}>
            <option value="none">None / bedrest / wheelchair</option>
            <option value="crutches_cane_walker">Crutches / cane / walker</option>
            <option value="furniture">Furniture</option>
          </NativeSelect>
        </div>
        <div>
          <label className="mb-1 block text-[11px] text-ink-muted">Gait</label>
          <NativeSelect className="w-28" value={String(items.gait ?? 'normal')} onChange={e => setItem('gait', e.target.value)}>
            <option value="normal">Normal</option>
            <option value="weak">Weak</option>
            <option value="impaired">Impaired</option>
          </NativeSelect>
        </div>
        <div>
          <label className="mb-1 block text-[11px] text-ink-muted">Mental status</label>
          <NativeSelect className="w-44" value={String(items.mental_status ?? 'oriented')} onChange={e => setItem('mental_status', e.target.value)}>
            <option value="oriented">Oriented to own ability</option>
            <option value="overestimates">Overestimates / forgets limits</option>
          </NativeSelect>
        </div>
      </div>
    );
  }
  if (tool === 'braden') {
    return (
      <div className="flex flex-wrap gap-3">
        {select4('sensory_perception', 'Sensory perception', 4)}
        {select4('moisture', 'Moisture', 4)}
        {select4('activity', 'Activity', 4)}
        {select4('mobility', 'Mobility', 4)}
        {select4('nutrition', 'Nutrition', 4)}
        {select4('friction_shear', 'Friction & shear', 3)}
      </div>
    );
  }
  if (tool === 'padua') {
    return (
      <div className="flex flex-wrap gap-3">
        {bool('active_cancer', 'Active cancer (3)')}
        {bool('previous_vte', 'Previous VTE (3)')}
        {bool('reduced_mobility', 'Reduced mobility (3)')}
        {bool('known_thrombophilia', 'Known thrombophilia (3)')}
        {bool('recent_trauma_or_surgery', 'Recent trauma/surgery (2)')}
        {bool('age_70_or_over', 'Age ≥70 (1)')}
        {bool('heart_or_respiratory_failure', 'Heart/resp. failure (1)')}
        {bool('acute_mi_or_stroke', 'Acute MI/stroke (1)')}
        {bool('acute_infection_or_rheumatologic', 'Acute infection/rheum. (1)')}
        {bool('obesity_bmi_30_or_over', 'BMI ≥30 (1)')}
        {bool('hormonal_treatment', 'Hormonal treatment (1)')}
      </div>
    );
  }
  if (tool === 'nrs2002') {
    return (
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-[11px] text-ink-muted">Impairment (0-3)</label>
          <NativeSelect className="w-16" value={String(items.impairment ?? 0)} onChange={e => setItem('impairment', Number(e.target.value))}>
            {[0, 1, 2, 3].map(n => <option key={n} value={n}>{n}</option>)}
          </NativeSelect>
        </div>
        <div>
          <label className="mb-1 block text-[11px] text-ink-muted">Severity (0-3)</label>
          <NativeSelect className="w-16" value={String(items.severity ?? 0)} onChange={e => setItem('severity', Number(e.target.value))}>
            {[0, 1, 2, 3].map(n => <option key={n} value={n}>{n}</option>)}
          </NativeSelect>
        </div>
        {bool('age_70_or_over', 'Age ≥70')}
      </div>
    );
  }
  if (tool === 'must') {
    return (
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-[11px] text-ink-muted">BMI score (0-2)</label>
          <NativeSelect className="w-16" value={String(items.bmi_score ?? 0)} onChange={e => setItem('bmi_score', Number(e.target.value))}>
            {[0, 1, 2].map(n => <option key={n} value={n}>{n}</option>)}
          </NativeSelect>
        </div>
        <div>
          <label className="mb-1 block text-[11px] text-ink-muted">Weight-loss score (0-2)</label>
          <NativeSelect className="w-16" value={String(items.weight_loss_score ?? 0)} onChange={e => setItem('weight_loss_score', Number(e.target.value))}>
            {[0, 1, 2].map(n => <option key={n} value={n}>{n}</option>)}
          </NativeSelect>
        </div>
        {bool('acute_disease_effect', 'Acute disease effect (+2)')}
      </div>
    );
  }
  if (tool === 'barthel') {
    const items10: [string, string, number][] = [
      ['feeding', 'Feeding', 10], ['bathing', 'Bathing', 5], ['grooming', 'Grooming', 5], ['dressing', 'Dressing', 10],
      ['bowels', 'Bowels', 10], ['bladder', 'Bladder', 10], ['toilet_use', 'Toilet use', 10],
      ['transfers', 'Transfers', 15], ['mobility', 'Mobility', 15], ['stairs', 'Stairs', 10],
    ];
    return (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {items10.map(([key, label, max]) => (
          <div key={key}>
            <label className="mb-1 block text-[11px] text-ink-muted">{label} (0-{max})</label>
            <Input type="number" min={0} max={max} className="w-16" value={String(items[key] ?? '')} onChange={e => setItem(key, Number(e.target.value))} />
          </div>
        ))}
      </div>
    );
  }
  return null;
}
